import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from "rxjs";
import { Announcement } from "../../services/announcement/announcement.models";
import { AnnouncementClientApiService } from "../../services/announcement/announcement-client-api.service";
import { AnnouncementModalComponent } from "./announcement-modal.component";
import { AnnouncementBannerComponent } from "./announcement-banner.component";
import { AnnouncementOverlayService } from "./announcement-overlay.service";
import { AnnouncementAudienceService } from "../../services/announcement/announcement-audience.service";

/**
 * Mounted once at the app shell root (app.component.html) — fetches the unseen, audience-matched
 * announcements for the current user on app open and shows them one at a time, in priority order.
 * This is the "shows directly after we open app" entry point; every announcement also stays
 * reachable from the header inbox afterwards (see AnnouncementInboxButtonComponent), which routes
 * back through here rather than rendering its own copy — see AnnouncementOverlayService for why.
 */
@Component({
  selector: "app-announcement-launcher",
  standalone: true,
  imports: [
    CommonModule,
    AnnouncementModalComponent,
    AnnouncementBannerComponent,
  ],
  templateUrl: "./announcement-launcher.component.html",
  styleUrl: "./announcement-launcher.component.scss",
})
export class AnnouncementLauncherComponent implements OnInit, OnDestroy {
  queue: Announcement[] = [];

  /** Re-opened from the header inbox; takes precedence over the auto-shown queue. */
  requested: Announcement | null = null;

  private requestedSub?: Subscription;

  constructor(
    private announcementClientApi: AnnouncementClientApiService,
    private overlay: AnnouncementOverlayService,
    private audience: AnnouncementAudienceService,
  ) {}

  get current(): Announcement | undefined {
    return this.requested ?? this.queue[0];
  }

  ngOnInit(): void {
    this.requestedSub = this.overlay.requested$.subscribe((announcement) => {
      this.requested = announcement;
    });
    // Small delay so the auto-popup doesn't fight the app shell / other first-paint work.
    window.setTimeout(() => this.loadInitialExperience(), 800);
  }

  ngOnDestroy(): void {
    this.requestedSub?.unsubscribe();
  }

  private loadQueue(): void {
    this.announcementClientApi.bootstrap().subscribe({
      next: (res) => {
        this.queue = res.body ?? [];
      },
      error: () => {
        this.queue = [];
      },
    });
  }

  /**
   * Onboarding panels are authored from the same announcement studio as product news, but carry
   * the TRIAL segment. Keep the client-side filter as a final guard: even if a stale backend sent
   * a CLIENT item to a brand-new company, it must never replace the getting-started experience.
   */
  private loadOnboardingQueue(): void {
    this.announcementClientApi.bootstrap().subscribe({
      next: (res) => {
        const onboarding = (res.body ?? []).filter(
          (announcement) => announcement.clientSegment === "TRIAL",
        );
        this.queue = onboarding;
      },
      error: () => {
        this.queue = [];
      },
    });
  }

  private loadInitialExperience(): void {
    this.audience.isNewCustomer().subscribe((isNewCustomer) => {
      if (!isNewCustomer) {
        this.loadQueue();
        return;
      }

      // New customers only receive onboarding content created in the announcement studio.
      // With no published TRIAL announcement, no popup is shown.
      this.loadOnboardingQueue();
    });
  }

  onClosed(): void {
    // A re-opened announcement is not part of the auto-shown queue — closing it must not
    // consume the next one waiting to be shown.
    if (this.requested) {
      this.overlay.close();
      return;
    }
    this.queue = this.queue.slice(1);
  }
}
